import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Lock, CheckCircle, ArrowRight } from "@phosphor-icons/react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!token) setErr("Falta el token de restablecimiento.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) {
      setErr("La contraseña debe tener al menos 6 caracteres.");
      return;
    }
    if (password !== confirm) {
      setErr("Las contraseñas no coinciden.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 2500);
    } catch (e2) {
      setErr(formatApiError(e2, "No se pudo restablecer la contraseña"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4">
      <div className="w-full max-w-md" data-testid="reset-password-container">
        <div className="mb-8 text-center">
          <div className="overline text-klein">Nueva contraseña</div>
          <div className="h-display text-5xl font-black tracking-tighter mt-1">
            Vende<span className="text-klein">IA</span>
          </div>
        </div>

        {done ? (
          <div
            className="border border-zinc-200 p-8 text-center space-y-3"
            data-testid="reset-success"
          >
            <CheckCircle size={42} weight="fill" className="text-[#00C853] mx-auto" />
            <div className="h-display text-xl font-bold">Contraseña actualizada</div>
            <p className="text-sm text-muted2">Te redirigimos al inicio de sesión…</p>
            <Link
              to="/login"
              className="inline-flex items-center gap-1 text-sm text-klein font-medium"
              data-testid="reset-go-login"
            >
              Ir ahora <ArrowRight size={14} />
            </Link>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="space-y-4 border border-zinc-200 p-8"
            data-testid="reset-form"
          >
            <div>
              <label className="overline block mb-2">Nueva contraseña</label>
              <div className="flex items-center border border-zinc-300 px-3 focus-within:border-klein">
                <Lock size={16} className="text-muted2" />
                <input
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="reset-password-input"
                  className="flex-1 px-3 py-3 text-sm outline-none"
                />
              </div>
            </div>

            <div>
              <label className="overline block mb-2">Confirmar contraseña</label>
              <div className="flex items-center border border-zinc-300 px-3 focus-within:border-klein">
                <Lock size={16} className="text-muted2" />
                <input
                  type="password"
                  required
                  minLength={6}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-confirm-input"
                  className="flex-1 px-3 py-3 text-sm outline-none"
                />
              </div>
            </div>

            {err && (
              <div
                className="text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-2"
                data-testid="reset-error"
              >
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !token}
              data-testid="reset-submit-btn"
              className="w-full bg-klein text-white py-3 text-sm font-medium hover:bg-klein/90 disabled:opacity-60"
            >
              {loading ? "Guardando…" : "Restablecer contraseña"}
            </button>

            <Link
              to="/login"
              className="block text-xs text-klein hover:underline"
              data-testid="reset-back-link"
            >
              Volver al login
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
